import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";

import { ICheckoutPatientDetail } from "@/models/patient";
import {
  IDoctor,
  ISpecialitiesList,
} from "@/models/ApiRequestResponse/patient";
import { IKeyPairsValues } from "@/models/doctor";

import {
  getDoctorsSpecialitiesListApi,
  getFindDoctorListApi,
  getSearchDoctorApi,
} from "@/utils/apis/patient/doctor";

import { RootState } from "@/store/store";

export const fetchDoctorSpecialities = createAsyncThunk(
  "patient/fetchDoctorSpecialities",
  async () => {
    const response = await getDoctorsSpecialitiesListApi();
    return response;
  }
);

const DOCTORS_PER_PAGE = 50;

export const fetchFindDoctor = createAsyncThunk(
  "patient/fetchFindDoctors",
  async ({
    params,
    page,
    signal,
  }: {
    params: string;
    page: number;
    signal: AbortSignal;
  }) => {
    const cleanedParams = params
      .replace(/&limit=[^&]*/g, "")
      .replace(/&offset=[^&]*/g, "");

    const offset = (page - 1) * DOCTORS_PER_PAGE;
    const fullParams = `${cleanedParams}&limit=${DOCTORS_PER_PAGE}&offset=${offset}`;

    const response = await getFindDoctorListApi(fullParams, signal);

    return { data: response, page };
  }
);

export const fetchSearchDoctors = createAsyncThunk(
  "patient/search-doctor",
  async (params: IKeyPairsValues) => {
    const response = await getSearchDoctorApi(params);
    return response;
  }
);

const initialState = {
  list: [] as IDoctor[],
  searchDoctors: [] as IDoctor[],
  checkoutPatientDetail: {} as ICheckoutPatientDetail,
  isLoading: false as boolean,
  isSearchDoctorLoading: false as boolean,
  specialitiesList: [] as ISpecialitiesList[],
  page: 1,
  hasMore: true,
};

const fetchFindDoctorSlice = createSlice({
  name: "fetchFindDoctors",
  initialState,
  reducers: {
    checkoutDetail: (state, action) => {
      state.checkoutPatientDetail = action.payload;
    },
    resetSearchedDoctors: (state) => {
      state.searchDoctors = [];
    },
    resetFindDoctorList: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFindDoctor.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchFindDoctor.fulfilled, (state, action) => {
        state.isLoading = false;
        if ("result" in action.payload.data) {
          const newDoctors = action.payload.data.result.doctors || [];

          if (newDoctors.length === 0) {
            state.hasMore = false;
            return;
          }

          state.list = [...state.list, ...newDoctors];
          state.page = action.payload.page + 1;
        } else {
          state.hasMore = false;
        }
      })
      .addCase(fetchFindDoctor.rejected, (state, action) => {
        state.isLoading = false;
        if (
          action.error.name !== "AbortError" &&
          action.error.name !== "CanceledError"
        ) {
          state.hasMore = false;
        }
      })
      .addCase(fetchSearchDoctors.pending, (state) => {
        state.isSearchDoctorLoading = true;
      })
      .addCase(fetchSearchDoctors.fulfilled, (state, action) => {
        state.isSearchDoctorLoading = false;
        if ("result" in action.payload) {
          state.searchDoctors = action.payload.result.doctors;
        }
      })
      .addCase(fetchSearchDoctors.rejected, (state) => {
        state.isSearchDoctorLoading = false;
      })
      .addCase(fetchDoctorSpecialities.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchDoctorSpecialities.fulfilled, (state, action) => {
        state.isLoading = false;
        if ("result" in action.payload) {
          state.specialitiesList = action.payload.result;
        }
      })
      .addCase(fetchDoctorSpecialities.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

const selectFindDoctor = (state: RootState) => state.findDoctor;

export const findDoctor = createSelector([selectFindDoctor], (findDoctor) => ({
  list: findDoctor.list,
  searchDoctors: findDoctor.searchDoctors,
  checkoutPatientDetail: findDoctor.checkoutPatientDetail,
  isLoading: findDoctor.isLoading,
  isSearchDoctorLoading: findDoctor.isSearchDoctorLoading,
  specialitiesList: findDoctor.specialitiesList,
  page: findDoctor.page,
  hasMore: findDoctor.hasMore,
}));

export const { checkoutDetail, resetSearchedDoctors, resetFindDoctorList } =
  fetchFindDoctorSlice.actions;
export default fetchFindDoctorSlice.reducer;
